/*
 * URL construction for the Catalog Graph link.
 *
 * Kept free of React so it can be tested directly. The query format is not a
 * free choice: CatalogGraphPage parses its query with qs and re-serializes it
 * with `arrayFormat: 'brackets'`, so arrays must be `key[]=value`.
 */

/**
 * The application-centric lens: domains own systems, systems are composed of
 * components, components provide and consume APIs - and components depend on
 * `resource`, which is why it is in this list.
 *
 * `resource` WAS excluded, to keep the starting view legible, and that quietly
 * emptied the graph of dependencies. The kinds list is not a display filter:
 * useEntityRelationGraph refuses to TRAVERSE a relation whose target kind is
 * unselected. In a discovered estate 310 of 318 dependency edges end at a
 * Resource, so excluding it meant the traversal could not leave an application
 * at all, and every app rendered as a leaf. The view was legible because it was
 * empty.
 */
const VIEW_KINDS = ['domain', 'system', 'component', 'api', 'resource'];

/**
 * One hop. The graph shows a NEIGHBOURHOOD, not a map.
 *
 * Depth and infrastructure cannot both be generous. Once `resource` is in scope
 * the catalog is 210 resources hanging off 23 systems, and depth is what turns
 * that into a hairball. Rendered label counts, measured in a browser against a
 * real 303-entity catalog:
 *
 *   view                        depth 1   depth 2   depth 3
 *   entry (four domain roots)        26       803         -
 *   one application                  11        25        43
 *
 * 803 is not a diagram. And the setting cannot be tuned per view: clicking a
 * node re-roots the page but carries the same kinds and depth over from this
 * URL, so whatever is chosen here has to work for the landing view AND for
 * every entity somebody drills into afterwards.
 *
 * Depth 1 is the only value that stays legible in both. The trade is real and
 * deliberate: the landing view shows domains and their systems rather than
 * reaching applications in one go. Applications are one click away, and arrive
 * with their infrastructure and their dependents attached - which is the thing
 * the view exists to show and previously could not.
 */
const MAX_DEPTH = 1;

/**
 * Draw edges in both directions.
 *
 * The page defaults `unidirectional` to true - the switch labelled "Simplified" -
 * which strips every edge that does not lead away from the root. That hides
 * exactly the relation somebody clicking an application is usually looking for:
 * what depends on THIS. It costs nothing to turn off, because traversal already
 * follows `dependencyOf`; only the drawing was suppressed.
 */
const UNIDIRECTIONAL = false;

export function graphHref(roots: string[]): string {
  if (roots.length === 0) {
    return '/catalog-graph';
  }
  // qs `brackets` array format, which is what the page parses its query with.
  const params = [
    ...roots.map(r => `rootEntityRefs[]=${encodeURIComponent(r)}`),
    ...VIEW_KINDS.map(k => `selectedKinds[]=${encodeURIComponent(k)}`),
    `maxDepth=${MAX_DEPTH}`,
    `unidirectional=${UNIDIRECTIONAL}`,
  ];
  return `/catalog-graph?${params.join('&')}`;
}
